import type {
  BlogPost,
  ComplianceItem,
  FAQItem,
  Feature,
  OnboardingStep,
  Stat,
  Testimonial,
} from "@/types"

interface NavLinkItem {
  label: string
  href: string
}

interface NavColumn {
  title: string
  links: NavLinkItem[]
}

interface DropdownConfig {
  columns: NavColumn[]
}

interface NavItem {
  label: string
  href: string
  dropdown?: DropdownConfig
}

export const NAV_ITEMS: NavItem[] = [
  {
    label: "Features",
    href: "/features",
    dropdown: {
      columns: [
        {
          title: "Payments",
          links: [
            { label: "Accept Payments", href: "/features/accept-payments" },
            { label: "Payment Gateway", href: "/features/gateway" },
            { label: "Subscriptions & Billing", href: "/features/subscriptions" },
            { label: "Invoicing", href: "/features/invoicing" },
          ],
        },
        {
          title: "Payouts & Money Movement",
          links: [
            { label: "Payouts & Disbursements", href: "/features/payouts" },
            { label: "Global Payouts", href: "/contact" },
            { label: "Instant Payouts", href: "/contact" },
            { label: "Mass Payouts", href: "/contact" },
          ],
        },
        {
          title: "Platform & Optimization",
          links: [
            { label: "Fraud & Risk", href: "/contact" },
            { label: "Smart Routing & Optimization", href: "/contact" },
            { label: "3D Secure & Authentication", href: "/contact" },
            { label: "Analytics & Reporting", href: "/contact" },
          ],
        },
      ],
    },
  },

  {
    label: "Solutions",
    href: "/contact",
    dropdown: {
      columns: [
        {
          title: "By Industry",
          links: [
            { label: "E-commerce", href: "/contact" },
            { label: "Marketplaces", href: "/contact" },
            { label: "SaaS", href: "/contact" },
            { label: "Gaming", href: "/contact" },
            { label: "Travel & Hospitality", href: "/contact" },
          ],
        },
        {
          title: "By Business Type",
          links: [
            { label: "Startups", href: "/contact" },
            { label: "SMBs", href: "/contact" },
            { label: "Enterprise", href: "/contact" },
            { label: "International / Cross-border", href: "/contact" },
          ],
        },
        {
          title: "Featured Solutions",
          links: [
            { label: "Embedded Payments & Finance", href: "/contact" },
            { label: "Global Payout Infrastructure", href: "/contact" },
            { label: "Subscription & Recurring Billing", href: "/contact" },
            { label: "High-risk & Regulated Verticals", href: "/contact" },
          ],
        },
      ],
    },
  },

  {
    label: "Developers",
    href: "https://docs.orchestrate.global",
    dropdown: {
      columns: [
        {
          title: "Get Started",
          links: [
            { label: "Quickstart Guide", href: "https://docs.orchestrate.global/#guides" },
            { label: "API Reference", href: "https://docs.orchestrate.global/payments" },
            { label: "SDKs & Libraries", href: "https://docs.orchestrate.global/sdks" },
            { label: "Sandbox & Test Cards", href: "https://docs.orchestrate.global/sandbox" },
            {
              label: "Merchant Categories",
              href: "https://docs.orchestrate.global/merchant-categories",
            },
          ],
        },
        {
          title: "Guides & Tools",
          links: [
            { label: "Webhooks & Events", href: "https://docs.orchestrate.global/webhooks" },
            { label: "Payments", href: "https://docs.orchestrate.global/payments" },
            {
              label: "Payment Processing",
              href: "https://docs.orchestrate.global/payments#create-a-payment",
            },
            { label: "Fraud Prevention Rules", href: "https://docs.orchestrate.global/fraud" },
            { label: "Migration Guides", href: "https://docs.orchestrate.global/migration" },
          ],
        },
        {
          title: "Support & Community",
          links: [
            { label: "API Changelog", href: "https://docs.orchestrate.global/changelog" },
            { label: "Developer Community", href: "https://docs.orchestrate.global/community" },
            { label: "Status Page", href: "https://status.orchestrate.global" },
          ],
        },
      ],
    },
  },

  {
    label: "Resources",
    href: "/contact",
    dropdown: {
      columns: [
        {
          title: "Learn",
          links: [
            { label: "Blog", href: "/blog" },
            { label: "Compliance", href: "/compliance" },
            { label: "Case Studies", href: "/contact" },
            { label: "Webinars & Events", href: "/contact" },
          ],
        },
        {
          title: "Support",
          links: [
            { label: "Help Center", href: "/contact" },
            { label: "Contact Support", href: "/contact" },
            { label: "Talk to Sales", href: "/contact" },
          ],
        },
      ],
    },
  },

  {
    label: "Pricing",
    href: "/pricing",
  },
]

export const FEATURES: Feature[] = [
  {
    id: "onboarding",
    number: "01",
    title: "Merchant Onboarding",
    description:
      "Streamlined KYC and certification flows that get merchants live fast. Includes document collection, screening, and continuous monitoring.",
    icon: "Users",
    color: "blue",
  },
  {
    id: "processing",
    number: "02",
    title: "Transaction Processing",
    description:
      "Cards, bank transfers, and wallets in one API. Precise and comprehensive, designed for high throughput and reliability at any scale.",
    icon: "CreditCard",
    color: "blue",
  },
  {
    id: "settlement",
    number: "03",
    title: "Settlement Engine",
    description:
      "Automated splits and payouts with configurable schedules, currencies, and fees. Set thresholds, holdbacks, and reconciliation rules.",
    icon: "DollarSign",
    color: "blue",
  },
  {
    id: "analytics",
    number: "04",
    title: "Analytics Dashboard",
    description:
      "Real-time metrics, reconciliation, and exportable reports. Drill-down views and CSV/API exports to power your BI stack.",
    icon: "Activity",
    color: "blue",
  },
]

export const STATS: Stat[] = [
  {
    id: "api-calls",
    value: "200",
    suffix: "M+",
    label: "API calls per minute",
    countTo: 200,
  },
  {
    id: "tpv",
    prefix: "$",
    value: "17",
    suffix: "B",
    label: "TPV monthly",
    countTo: 17,
  },
  {
    id: "daily",
    value: "26",
    suffix: "M",
    label: "Payments processed daily",
    countTo: 26,
  },
]

export const TRUSTED_BY = [
  { name: "AiTechMA", logo: "/images/partners/aitechma.c3246158.svg" },
  { name: "Akupay", logo: "/images/partners/akupay.svg" },
  { name: "Cadri", logo: "/images/partners/cadri.a06dec64.svg" },
  { name: "Finite", logo: "/images/partners/finite.9f50e1bf.svg" },
  { name: "Futures", logo: "/images/partners/futures.b380bad2.svg" },
  { name: "Globpay", logo: "/images/partners/globpay.png" },
  { name: "GoFree", logo: "/images/partners/gofree.7733ffa9.svg" },
  { name: "GoPrem", logo: "/images/partners/goprem.d099343e.svg" },
  { name: "MoniRates", logo: "/images/partners/monirates.420e7fb2.svg" },
  { name: "Pandar", logo: "/images/partners/pandar.6ff82a3b.svg" },
  { name: "TellerOne", logo: "/images/partners/tellerone.0c0e1046.svg" },
  { name: "TYMS", logo: "/images/partners/tyms.aa0cc29b.svg" },
  { name: "Vant", logo: "/images/partners/vant.bb791472.svg" },
  { name: "Westpoint", logo: "/images/partners/westpoint.d75e63e2.svg" },
]

export const ONBOARDING_STEPS: OnboardingStep[] = [
  {
    step: 1,
    title: "Application",
    description:
      "Submit business information and required documentation through our secure portal.",
  },
  {
    step: 2,
    title: "Due Diligence",
    description:
      "Comprehensive KYC and AML checks to ensure regulatory compliance and risk assessment.",
  },
  {
    step: 3,
    title: "Underwriting",
    description: "Risk classification and collateral requirements based on your business model.",
  },
  {
    step: 4,
    title: "Integration & Go-Live",
    description: "API support, sandbox access, and production deployment assistance from our team.",
  },
]

export const COMPLIANCE_ITEMS: ComplianceItem[] = [
  {
    id: "prohibited",
    label: "Prohibited Countries",
    status: "blocked",
    description: "Transactions blocked automatically at API level",
    color: "rose",
  },
  {
    id: "restricted",
    label: "Restricted — EDD Required",
    status: "edd",
    description: "Enhanced due diligence mandatory for processing",
    color: "amber",
  },
  {
    id: "controlled",
    label: "Controlled — Whitelisted Only",
    status: "controlled",
    description: "Pre-approved merchant list required for access",
    color: "green",
  },
]

export const CERTIFICATIONS = [
  { label: "PCI DSS", sub: "Level 1" },
  { label: "GDPR", sub: "Compliant" },
  { label: "SOC 2", sub: "Type II" },
  { label: "ISO 27001", sub: "Certified" },
  { label: "AML", sub: "Screened" },
  { label: "KYC", sub: "Verified" },
]

export const TESTIMONIALS: Testimonial[] = [
  {
    id: "t1",
    quote:
      "Orchestrate cut our time-to-live for new merchants from 3 weeks to under 48 hours. The compliance tooling alone was worth the switch.",
    author: "Samira Okafor",
    role: "CTO",
    company: "Cadri",
    initials: "SO",
    color: "bg-blue",
  },
  {
    id: "t2",
    quote:
      "The settlement engine is exceptional. We've completely automated our split payouts across 12 currencies with zero manual intervention.",
    author: "Marcus Reinholt",
    role: "VP Engineering",
    company: "TellerOne",
    initials: "MR",
    color: "bg-green-DEFAULT",
  },
  {
    id: "t3",
    quote:
      "Fraud rates dropped by 87% in the first month after switching. The ML scoring adapts to our traffic patterns automatically.",
    author: "Priya Shankar",
    role: "Head of Payments",
    company: "MoniRates",
    initials: "PS",
    color: "bg-amber-DEFAULT",
  },
]

export const FAQS: FAQItem[] = [
  {
    id: "faq1",
    question: "How long does merchant onboarding take?",
    answer:
      "Standard onboarding takes 5–10 business days, depending on business complexity and documentation completeness. High-risk categories may require additional due diligence.",
  },
  {
    id: "faq2",
    question: "What payment methods does Orchestrate support?",
    answer:
      "We support all major card networks (Visa, Mastercard, Amex, Discover), SEPA and ACH bank transfers, digital wallets (Apple Pay, Google Pay), and 40+ local payment methods globally.",
  },
  {
    id: "faq3",
    question: "How is pricing structured?",
    answer:
      "We offer competitive, volume-based pricing with transparent interchange-plus fees. Contact our sales team for a custom quote based on your monthly volume and business model.",
  },
  {
    id: "faq4",
    question: "What compliance certifications does Orchestrate hold?",
    answer:
      "Orchestrate maintains PCI DSS Level 1 certification, SOC 2 Type II attestation, GDPR compliance, and ISO 27001 certification. Our compliance posture is audited annually.",
  },
  {
    id: "faq5",
    question: "Do you offer a sandbox environment?",
    answer:
      "Yes. Every account includes full sandbox access with simulated card responses, webhook testing, and mirrored production API behavior. No approval needed to start testing.",
  },
  {
    id: "faq6",
    question: "What SLA does Orchestrate offer?",
    answer:
      "We guarantee 99.9% uptime across all critical API endpoints, backed by enterprise-grade infrastructure, geo-redundant deployments, and a dedicated incident response team.",
  },
]

export const BLOG_POSTS: BlogPost[] = [
  {
    id: "b1",
    slug: "understanding-payment-orchestration",
    title: "Understanding Payment Orchestration in 2026",
    excerpt:
      "How modern payment orchestration layers help businesses reduce costs, improve approval rates, and accelerate global expansion.",
    category: "Insights",
    date: "Feb 18, 2026",
    readTime: "7 min read",
    author: "Orchestrate Team",
  },
  {
    id: "b2",
    slug: "pci-dss-v4-what-you-need-to-know",
    title: "PCI DSS v4.0: What Merchants Need to Know",
    excerpt:
      "A practical breakdown of the latest PCI DSS requirements and how Orchestrate handles compliance so you don't have to.",
    category: "Compliance",
    date: "Feb 10, 2026",
    readTime: "5 min read",
    author: "Compliance Team",
  },
  {
    id: "b3",
    slug: "reducing-chargebacks-with-dynamic-descriptors",
    title: "Reducing Chargebacks with Dynamic Descriptors",
    excerpt:
      "How customizing your transaction descriptor can significantly reduce dispute rates and improve customer recognition.",
    category: "Product",
    date: "Jan 28, 2026",
    readTime: "4 min read",
    author: "Product Team",
  },
]

export const CHART_BARS = [45, 62, 50, 80, 55, 90, 70, 100] as const
